import { faMinus as lightMinus } from "@jewlr/pro-light-svg-icons/faMinus"
import { faPlus as lightPlus } from "@jewlr/pro-light-svg-icons/faPlus"
import { FocusScope } from "@react-aria/focus"
import { createSelector } from "@reduxjs/toolkit"
import PropTypes from "prop-types"
import { memo, useCallback } from "react"
import { useSelector, useDispatch } from "react-redux"

import { togglePanelIndex } from "areas/product/store/actions"
import Button from "components/ui/button"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import { isTestMode } from "helpers/application"
import { to_currency } from "helpers/number"

import Thumbnail from "./thumbnail"

const selectTotalStonePrice = (prefs) =>
  createSelector([(state) => state.choices], (choices) =>
    prefs.reduce((sum, pref) => {
      const choice = choices[pref]
      return sum + (choice?.display_price || choice?.price || 0)
    }, 0)
  )

const GuidedStoneSelectorHeading = ({ currentPref, handleGoToPref, prefs }) => {
  const dispatch = useDispatch()

  const isOpened = useSelector((state) =>
    state.panel.openedPanelIndexes.includes("stone-selector")
  )

  const totalStonePrice = useSelector(selectTotalStonePrice(prefs))
  const styleCode = useSelector((state) => state.choices.style_code?.value)

  const togglePanel = useCallback(() => {
    if (!isTestMode) {
      dataLayer.push({
        event: "opened-stone-selector",
        optionName: null,
        styleCode,
      })
    }

    dispatch(togglePanelIndex("stone-selector", "stone-selector", 1))
  }, [dispatch])

  return (
    <div>
      <h2 className="py-4">
        <Button
          aria-controls="wizard-accordion-content-stone-selector"
          aria-expanded={isOpened}
          className="leading-18 flex w-full justify-between py-12 text-left outline-offset-0"
          data-cy="product-wizard-heading-stone-selector"
          id="wizard-accordion-heading-stone-selector"
          onClick={togglePanel}
        >
          <p className="text-14 font-semibold">Stone Selection</p>
          <div className="itesm-center flex">
            {totalStonePrice > 0 && (
              <p
                className="text-14 tracking-half mr-20 font-bold"
                data-ge-subproducts-subsequent-price
              >
                {to_currency(totalStonePrice)}
              </p>
            )}
            <FontAwesomeIcon
              className="text-grey-65 text-16"
              icon={isOpened ? lightMinus : lightPlus}
            />
          </div>
        </Button>
      </h2>
      <FocusScope>
        <ul
          aria-label="Stone selection quick navigation"
          className="flex list-none flex-wrap justify-start gap-12 pb-12"
        >
          {prefs.map((pref, i) => {
            return (
              <li key={`stone-selector-thumbnail-${pref}`}>
                <Thumbnail
                  currentPref={currentPref}
                  handleGoToPref={handleGoToPref}
                  index={i}
                  pref={pref}
                />
              </li>
            )
          })}
        </ul>
      </FocusScope>
    </div>
  )
}

GuidedStoneSelectorHeading.propTypes = {
  currentPref: PropTypes.string,
  handleGoToPref: PropTypes.func,
  prefs: PropTypes.array,
}

export default memo(GuidedStoneSelectorHeading)
